import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, Navigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Stethoscope, Users, DollarSign, Share2,
  Settings, LogOut, Menu, X, Layers, GraduationCap, ShieldCheck, Wallet, Inbox,
} from "lucide-react";

const LINKS = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/dashboard/physicians", label: "My Physicians", icon: Stethoscope },
  { to: "/dashboard/leads", label: "Leads", icon: Inbox },
  { to: "/dashboard/earnings", label: "Earnings", icon: DollarSign },
  { to: "/dashboard/team", label: "My Team", icon: Users },
  { to: "/dashboard/compensation", label: "Compensation", icon: Layers },
  { to: "/dashboard/share", label: "Share & Grow", icon: Share2 },
  { to: "/dashboard/training", label: "Training", icon: GraduationCap },
  { to: "/dashboard/settings", label: "Settings", icon: Settings },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (user && !user.activated) return <Navigate to="/activate" replace />;

  const links = user?.role === "admin"
    ? [...LINKS, { to: "/dashboard/admin", label: "Admin", icon: ShieldCheck }, { to: "/dashboard/payouts", label: "Payouts", icon: Wallet }]
    : LINKS;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const SidebarContent = (
    <div className="flex flex-col h-full">
      <div className="px-6 py-5 border-b border-border">
        <Logo />
      </div>
      <nav className="flex-1 p-4 space-y-1">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            onClick={() => setOpen(false)}
            data-testid={`sidebar-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive ? "bg-navy text-white" : "text-muted-foreground hover:bg-muted hover:text-navy"
              }`
            }
          >
            <l.icon className="h-[18px] w-[18px]" /> {l.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-border">
        <div className="px-4 py-3 mb-2">
          <p className="text-sm font-semibold text-navy truncate">{user?.name}</p>
          <p className="text-xs text-muted-foreground font-mono">{user?.referral_code}</p>
        </div>
        <Button onClick={handleLogout} variant="ghost" data-testid="logout-btn" className="w-full justify-start gap-3 text-muted-foreground hover:text-patriot hover:bg-patriot/5">
          <LogOut className="h-[18px] w-[18px]" /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden lg:flex w-72 bg-white border-r border-border fixed inset-y-0 z-40">{SidebarContent}</aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-72 bg-white h-full">{SidebarContent}</div>
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 lg:ml-72">
        <header className="lg:hidden bg-white border-b border-border px-5 h-16 flex items-center justify-between sticky top-0 z-30">
          <Logo showText={false} />
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn"><Menu className="h-6 w-6 text-navy" /></button>
        </header>
        <main className="p-5 sm:p-8 max-w-6xl">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
